import { useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  documents as docsApi,
  sources as sourcesApi,
  type SourceDocumentRow,
  type SourceKind,
} from "@/lib/api";

const KIND_LABEL: Record<SourceKind, string> = {
  brief: "Kickoff brief",
  reference: "Reference",
  transcript: "Transcript",
  data: "Data",
};

const KIND_BADGE: Record<SourceKind, string> = {
  brief: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  reference: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  transcript: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  data: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200",
};

export function Sources() {
  const { docId } = useParams<{ docId: string }>();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [kind, setKind] = useState<SourceKind>("reference");
  const [title, setTitle] = useState("");

  const docQuery = useQuery({
    queryKey: ["document", docId],
    queryFn: () => docsApi.get(docId!),
    enabled: !!docId,
  });

  const sourcesQuery = useQuery({
    queryKey: ["sources", docId],
    queryFn: () => sourcesApi.list(docId!),
    enabled: !!docId,
  });

  const uploadMut = useMutation({
    mutationFn: (vars: { file: File; kind: SourceKind; title?: string }) =>
      sourcesApi.upload(docId!, vars.file, { kind: vars.kind, title: vars.title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sources", docId] });
      setTitle("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });

  const deleteMut = useMutation({
    mutationFn: (sourceId: string) => sourcesApi.delete(docId!, sourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sources", docId] });
    },
  });

  if (!docId) return <div className="p-8">Missing document id</div>;
  const doc = docQuery.data?.document;
  const list = sourcesQuery.data?.sources ?? [];
  const briefCount = list.filter((s) => s.kind === "brief").length;

  const handleUpload = (e: FormEvent) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    uploadMut.mutate({ file, kind, title: title.trim() || undefined });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <Link
            to={`/d/${docId}`}
            className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            ← {doc?.title ?? "Document"}
          </Link>
          <h1 className="text-2xl font-semibold mt-1">Sources</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Briefs, references, transcripts, and data the agents draw on. Upload markdown / plain text or PDF (≤ 10 MB).
          </p>
        </div>
      </header>

      {briefCount === 0 && (
        <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm mb-6">
          <span className="font-medium text-amber-900 dark:text-amber-200">No kickoff brief yet.</span>{" "}
          <span className="text-amber-800 dark:text-amber-300">
            Upload a brief (kind: <span className="font-mono">brief</span>) so the agents know what to draft.
          </span>
        </div>
      )}

      {/* Upload form */}
      <form
        onSubmit={handleUpload}
        className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4 mb-6"
      >
        <div className="text-sm font-medium mb-3">Upload a source</div>
        <div className="grid sm:grid-cols-3 gap-3 items-start">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-zinc-500">Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as SourceKind)}
              className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
            >
              {(Object.keys(KIND_LABEL) as SourceKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-wide text-zinc-500">
              Title (optional, defaults to filename)
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown,.txt,.pdf,text/markdown,text/plain,application/pdf"
            className="text-sm flex-1"
            required
          />
          <button
            type="submit"
            disabled={uploadMut.isPending}
            className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 disabled:opacity-50"
          >
            {uploadMut.isPending ? "Uploading…" : "Upload"}
          </button>
        </div>
        {uploadMut.error && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400">
            {apiErrText(uploadMut.error)}
          </div>
        )}
      </form>

      {/* Source list */}
      <section>
        <div className="text-sm font-medium mb-2">Library ({list.length})</div>
        {sourcesQuery.isLoading && (
          <div className="text-sm text-zinc-500">Loading…</div>
        )}
        {!sourcesQuery.isLoading && list.length === 0 && (
          <div className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 p-6 text-center text-sm text-zinc-500">
            No sources yet.
          </div>
        )}
        {list.length > 0 && (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-md border border-zinc-200 dark:border-zinc-800">
            {list.map((src) => (
              <SourceRow
                key={src.id}
                src={src}
                docId={docId}
                onDelete={() => deleteMut.mutate(src.id)}
                deleting={deleteMut.isPending && deleteMut.variables === src.id}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SourceRow({
  src,
  docId,
  onDelete,
  deleting,
}: {
  src: SourceDocumentRow;
  docId: string;
  onDelete: () => void;
  deleting: boolean;
}) {
  const handleDownload = async () => {
    try {
      const res = await sourcesApi.download(docId, src.id);
      if (res.kind === "presigned" && res.url) {
        window.open(res.url, "_blank");
      } else if (res.kind === "inline" && res.body !== undefined) {
        // Open body in a new tab as plain text
        const blob = new Blob([res.body], { type: "text/plain" });
        window.open(URL.createObjectURL(blob), "_blank");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("download failed", e);
    }
  };
  return (
    <li className="px-4 py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${KIND_BADGE[src.kind]}`}
          >
            {KIND_LABEL[src.kind]}
          </span>
          <span className="font-medium truncate">{src.title}</span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {src.created_at && new Date(src.created_at).toLocaleString()}
          {src.uploaded_by && (
            <>
              {" · "}
              by {src.uploaded_by.slice(0, 8)}…
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={handleDownload}
          className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1 hover:bg-zinc-50 dark:hover:bg-zinc-900"
        >
          {src.downloadable ? "Download" : "View"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
        >
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </li>
  );
}

function apiErrText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | string | null;
    if (typeof body === "string") return body;
    return body?.error ?? `HTTP ${err.status}`;
  }
  return String(err);
}
